import { useState, useEffect } from "react";
import { Outlet } from "react-router-dom";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/shared/components/layout/AppSidebar";
import { Breadcrumbs } from "@/shared/components/layout/Breadcrumbs";
import { BreadcrumbDevGuard } from "@/shared/components/layout/BreadcrumbDevGuard";
import { Bell, FileText, AlertCircle, Search, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { useSearch } from "@/contexts/SearchContext";
import { useAuth } from "@/contexts/AuthContext";
import { useOrgCacheCleanup } from "@/hooks/useOrgCacheCleanup";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const MOCK_NOTIFICATIONS = [
  {
    id: 1,
    title: 'Upcoming Task Tomorrow',
    message: 'Day 2 task for Arbitration – JANA – Case #5678',
    hoursAgo: 2,
    priority: 'high',
    icon: Bell
  },
  {
    id: 2,
    title: 'Documents Ready to Download',
    message: '5 new conciliation documents are available for download.',
    hoursAgo: 5,
    priority: 'normal',
    icon: FileText
  },
  {
    id: 3,
    title: 'Deadline Approaching',
    message: 'Step deadline in 2 days for Arbitration – HDFC – Case #3456',
    hoursAgo: 24,
    priority: 'warning',
    icon: AlertCircle
  }
];

function getRelativeTime(hoursAgo: number) {
  const days = Math.floor(hoursAgo / 24);
  if (days > 0) return `${days} day${days > 1 ? 's' : ''} ago`;
  if (hoursAgo > 0) return `${hoursAgo} hour${hoursAgo > 1 ? 's' : ''} ago`;
  return 'Just now';
}

export function Layout() {
  const { searchQuery, setSearchQuery, placeholder } = useSearch();
  const { activeBusiness, singleBusinessMode } = useAuth();
  useOrgCacheCleanup();

  // Debounced search: local state updates instantly, context updates after 300ms
  const [localSearch, setLocalSearch] = useState(searchQuery);
  useEffect(() => {
    const timer = setTimeout(() => setSearchQuery(localSearch), 300);
    return () => clearTimeout(timer);
  }, [localSearch, setSearchQuery]);
  // Sync external resets (e.g. clearSearch) — intentionally only reacts to searchQuery
  useEffect(() => {
    if (searchQuery === '') setLocalSearch('');
     
  }, [searchQuery]);

  return (
    <SidebarProvider>
      <div className="flex h-screen w-full overflow-hidden">
        <AppSidebar />
        <div className="flex-1 flex flex-col min-w-0">
          <header className="h-14 border-b border-border bg-background flex items-center px-4 shrink-0 sticky top-0 z-10">
            {/* Left section - Sidebar trigger + active business indicator */}
            <div className="flex items-center">
              <SidebarTrigger />
              {activeBusiness && !singleBusinessMode && (
                <div className="ml-3 flex items-center gap-1.5 text-sm">
                  <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="font-medium text-foreground/80">{activeBusiness.businessName}</span>
                </div>
              )}
            </div>
            
            {/* Center section - Search bar (centered) */}
            <div className="flex-1 flex justify-center">
              <div className="w-full max-w-md">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="search"
                    placeholder={placeholder}
                    value={localSearch}
                    onChange={(e) => setLocalSearch(e.target.value)}
                    className="pl-9 w-full"
                  />
                </div>
              </div>
            </div>
            
            {/* Right section - Notifications */}
            <div className="flex items-center">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="relative">
                    <Bell className="h-5 w-5" />
                    <Badge
                      variant="destructive"
                      className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-[10px]"
                    >
                      {MOCK_NOTIFICATIONS.length}
                    </Badge>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-96 bg-background z-50">
                  <div className="p-4">
                    <h3 className="font-semibold mb-3">Notifications</h3>
                    <div className="space-y-2">
                      {MOCK_NOTIFICATIONS.map(notif => {
                        const Icon = notif.icon;
                        return (
                          <DropdownMenuItem key={notif.id} className="p-3 cursor-pointer">
                            <div className="flex items-start gap-3 w-full">
                              <div className={`p-2 rounded-full ${
                                notif.priority === 'high' ? 'bg-red-100' :
                                notif.priority === 'warning' ? 'bg-yellow-100' :
                                'bg-blue-100'
                              }`}>
                                <Icon className={`h-4 w-4 ${
                                  notif.priority === 'high' ? 'text-red-600' :
                                  notif.priority === 'warning' ? 'text-yellow-600' :
                                  'text-blue-600'
                                }`} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm">{notif.title}</p>
                                <p className="text-xs text-muted-foreground line-clamp-2">{notif.message}</p>
                                <p className="text-xs text-muted-foreground mt-1">{getRelativeTime(notif.hoursAgo)}</p>
                              </div>
                            </div>
                          </DropdownMenuItem>
                        );
                      })}
                    </div>
                    <Button variant="outline" className="w-full mt-3" size="sm">
                      View All Notifications
                    </Button>
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </header>
          <main className="flex-1 flex flex-col p-6 bg-background overflow-y-auto overflow-x-hidden max-w-full [&>*]:max-w-full">
            <Breadcrumbs />
            {import.meta.env.DEV && <BreadcrumbDevGuard />}
            <Outlet />
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}
